import { Injectable } from '@angular/core';
import {ApiService, ClaimTypeAndValue, UserDef} from "../../services/api.service";
import {
  catchError,
  map,
  Observable,
  Subject,
  switchMap,
  throwError,
} from "rxjs";
import { Router } from "@angular/router";


export interface AuthLogonRequest {
  username: string
  password: string
}

export interface AuthPasswordResetRequest {
  oldPassword: string
  newPassword: string
}

export interface AuthTokenResult {
  token: string
}

export interface AuthIdentityClaim {
  type: string
  value?: string
}

export interface AuthIdentity {
  sub: string
  claims?: AuthIdentityClaim[]
  exp: number
  type?: string
  nbf?: number
  iat?: number
  jti?: string
  fresh?: boolean
  csrf?: string
}

export interface AuthSession {
  accessToken: string
  identity: AuthIdentity
}


@Injectable({
  providedIn: 'root'
})
export class AuthService {

  protected readonly ClaimTypeRole = 'role'

  protected readonly RoleNameSuperUser = 'Super'

  protected readonly RoleNameAdminUser = 'Admin'

  get systemRoleNames(): string[] {
    return [
      this.RoleNameSuperUser,
      this.RoleNameAdminUser,
    ]
  }

  protected _session_key = "rag.dashboard.auth.session"

  protected _store: Storage

  public readonly sessionState: Subject<AuthSession | undefined> = new Subject<AuthSession | undefined>();

  constructor(
    readonly api: ApiService,
    readonly router: Router
  ) {
    this._store = window.localStorage
  }

  getExpirationDate(identity: AuthIdentity): Date {
    return new Date(identity.exp * 1000)
  }

  isExpired(identity: AuthIdentity, toleranceInSeconds: number = 60): boolean {
    const now = new Date()
    const expiration = this.getExpirationDate(identity)
    const diff = expiration.getTime() - now.getTime()
    return diff <= toleranceInSeconds * 1000
  }

  authenticate(username: string, password: string): Observable<AuthIdentity> {
    return this.api.httpPost<AuthLogonRequest, any>('auth/token', {
      username: username,
      password: password
    }).pipe(
      catchError((e) => {
        return throwError(e)
      }),
      switchMap(result => {
        return this.loadIdentity(result)
      })
    )
  }

  resetPassword(oldPassword: string, newPassword: string): Observable<any> {
    const session = this.getSession()
    if (!session) {
      return throwError(() => new Error('Not logged in'))
    }
    return this.api.httpPost<AuthPasswordResetRequest, any>('auth/reset-password', {
      oldPassword: oldPassword,
      newPassword: newPassword
    })
  }

  loadIdentity(tokenResult: AuthTokenResult): Observable<AuthIdentity> {
    return this.api.httpGet<AuthIdentity>('auth/id', {
      headers: {
        Authorization: `Bearer ${tokenResult.token}`,
      },
      responseType: 'json'
    }).pipe(
      map(result => {
          this.persistIdentity(tokenResult.token, result)
          return result
      })
    )
  }

  protected persistIdentity(accessToken: string, identity: AuthIdentity) {
    const session: AuthSession = {
      accessToken: accessToken,
      identity: identity
    }
    if (this.isExpired(session.identity)) {
      console.error('Session has expired', session)
    }
    else {
      this._store.setItem(this._session_key, JSON.stringify(session))
      this.sessionState.next(session)
      console.info('Session established')
    }
  }

  /** Gets the session object for the current user, if there is any. */
  getSession(): AuthSession | undefined {
    try {
      const sessionString = this._store.getItem(this._session_key)
      if (sessionString && sessionString.length) {
        const session: AuthSession = JSON.parse(sessionString)
        if (this.isExpired(session.identity)) {
          console.warn('Session has expired', session)
          return undefined
        }
        return session
      }
    }
    catch (e){
      console.error('Failed to retrieve session data from storage', e)
    }
    finally {
    }
    return undefined
  }

  /** Gets an indication, if the current user has an established claims identity session. */
  isAuthenticated(): boolean {
    return this.getSession() != undefined
  }

  /** Gets an indication, if the current user has an established claims identity session and has role claims
   * that match at least one value in the target role values range. */
  hasRoleInRange(roleRange: string[]): boolean {
    if (!roleRange.length) {
      return true
    }
    const session = this.getSession()
    if (session && session.identity && session.identity.claims) {
      const claims: AuthIdentityClaim[] = session.identity.claims || []
      const roleClaims: string[] = claims.filter(c => c.type == this.ClaimTypeRole).map(c => c.value || '')
      return roleRange.some((ar) => roleClaims.includes(ar))
    }
    return false
  }

  isEntityOwner(entity: { createdBy: string }) {
    try {
      return entity.createdBy === this.getSessionUserId()
    }
    catch (e) {
      console.error('Error in isEntityOwner:', e, 'entity', entity)
      return false
    }
  }

  isOwnerOrMatchesCriteria(
    entity: { createdBy: string },
    rolesInRange?: string[],
    claimsInRange?: ClaimTypeAndValue[]) {
    if (this.isEntityOwner(entity)) {
      return true
    }
    if (rolesInRange && rolesInRange.length && this.hasRoleInRange(rolesInRange)) {
      return true
    }
    if (claimsInRange && claimsInRange.length && this.hasClaimInRange(claimsInRange)) {
      return true
    }
    return false
  }

  hasClaimInRange(claimsRange: ClaimTypeAndValue[]) {
    const session = this.getSession()
    if (!session) return false
    const claims = session.identity.claims || []
    claims.forEach((c) => {
      if (this.hasClaim(c.type, c.value)) {}
      return true
    })
    return false
  }

  hasClaim(claimType: string, claimValue?: any) {
    const session = this.getSession()
    if (session && session.identity && session.identity.claims) {
      const claims: AuthIdentityClaim[] = session.identity.claims || []
      const claimsValuesForType: any[] = claims.filter(c => c.type == claimType)
        .map(c => c.value || null)
      if (claimValue != undefined) {
        return claimsValuesForType.includes(claimValue)
      }
      else {
        const result = claimsValuesForType.length > 0
        if (!result) {
          console.warn('Claim not found', claimType)
        }
        return result
      }
    }
    return false
  }

  isSuperUser(): boolean {
    return this.hasRoleInRange([
      this.RoleNameSuperUser
    ])
  }

  isAdminUser(): boolean {
    return this.hasRoleInRange([
      this.RoleNameAdminUser
    ])
  }

  getSessionUserId(): string | undefined {
    const session = this.getSession()
    return session?.identity?.sub
  }

  /** Clears out the user active session, if any. Navigates to the root '/' route. */
  logout(): void {
    const session = this.getSession()
    if (! session || this.isExpired(session.identity)) {
      console.error('Session has expired', session)
    }
    else {
      console.warn('Clearing out session...')
      this._store.removeItem(this._session_key)
      this.sessionState.next(undefined)
      console.info("Session terminated.", this.getSession())
      this.router.navigate(['/']).finally(() => {})
    }
  }

  canAddRole(roleName: string, targetUser: UserDef): boolean {
    if (targetUser.claims) {
      if (targetUser.claims.find(c => c.claim == this.ClaimTypeRole && c.value == roleName)) {
        return false
      }
    }
    return this.isSuperUser()
  }

  canRemoveRole(roleName: string, targetUser: UserDef): boolean {
    if (!this.isSuperUser()) {
      return false
    }
    if (targetUser.isSystem) {
      return false
    }
    if (targetUser.isPendingNew === true) {
      return true
    }
    if (targetUser.claims) {
      if (roleName == this.RoleNameSuperUser && targetUser.isSystem &&
        targetUser.claims.find(c => c.claim == this.ClaimTypeRole && c.value == this.RoleNameSuperUser)) {
        return false
      }
      if (!targetUser.claims.find(c => c.claim == this.ClaimTypeRole && c.value == roleName)) {
        console.warn('cannot remove missing role', roleName)
        return false
      }
    }
    return true
  }
}
